import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useWaitlist } from "@/context/WaitlistContext";
import { joinWaitlist } from "@/lib/api";

// Waitlist accepts a set of allowed providers. The local part must be alphanumeric
// segments separated by single dots. Rejects typo/other domains, empty local part,
// double @, and leading/trailing/consecutive dots.
const ALLOWED_EMAIL_RE = /^[a-z0-9]+(?:\.[a-z0-9]+)*@(?:gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|example\.com)$/;
const INVALID_EMAIL_MESSAGE = "Please enter a valid email address (example@gmail.com).";
const INVALID_NAME_MESSAGE = "Please enter your name.";

const isValidEmail = (value) => {
  const email = (value || "").trim().toLowerCase();
  if (!email || email.length > 254) return false;
  return ALLOWED_EMAIL_RE.test(email);
};

const WaitlistModal = () => {
  const { open, setOpen } = useWaitlist();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName("");
    setEmail("");
    setError("");
    setNameError("");
    setLoading(false);
    setDone(false);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNameError("");
    const cleanName = name.trim();
    const value = email.trim().toLowerCase();
    if (cleanName.length < 2) {
      setNameError(INVALID_NAME_MESSAGE);
      return;
    }
    if (!isValidEmail(value)) {
      setError(INVALID_EMAIL_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      const res = await joinWaitlist(cleanName, value);
      if (res.status === "already_subscribed") {
        toast.info("You're already on the list!", { description: "We'll be in touch soon." });
        setDone(true);
      } else {
        toast.success("You're on the list!", { description: "Check your inbox for confirmation." });
        setDone(true);
      }
    } catch (err) {
      // Surface the backend's validation message (e.g. invalid email) when present.
      const serverMsg = err?.response?.data?.detail;
      setError(typeof serverMsg === "string" ? serverMsg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="waitlist-modal">
        {done ? (
          <div className="flex flex-col items-center py-4 text-center" data-testid="waitlist-success-state">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: "linear-gradient(135deg, rgba(80,227,194,0.18), rgba(74,144,226,0.14))" }}>
              <CheckCircle2 className="h-7 w-7 text-[#0f9b82]" />
            </div>
            <DialogTitle className="font-display text-xl">You’re on the list!</DialogTitle>
            <DialogDescription className="mt-1.5" data-testid="waitlist-success-message">
              Check your inbox for confirmation — we’ve sent a welcome email to{" "}
              <span className="font-medium text-slate-700">{email}</span>.
            </DialogDescription>
            <Button
              onClick={() => handleOpenChange(false)}
              className="mt-6 w-full rounded-xl bg-[var(--jb-blue)] text-white transition-colors hover:bg-[#3E7FCA]"
              data-testid="waitlist-close-button"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#4A90E2,#50E3C2)" }}>
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <DialogTitle className="font-display text-xl">Join the JedBot waitlist</DialogTitle>
              <DialogDescription>
                Get early access, launch updates, and a head start with your AI companion.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="mt-2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="waitlist-name">Name</Label>
                <Input
                  id="waitlist-name"
                  type="text"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="h-11 rounded-xl focus-visible:ring-2 focus-visible:ring-[rgba(74,144,226,0.35)]"
                  data-testid="waitlist-name-input"
                  aria-invalid={!!nameError}
                  maxLength={80}
                />
                {nameError && (
                  <p role="alert" className="text-sm text-red-600" data-testid="waitlist-name-error-text">{nameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-email">Email address</Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="h-11 rounded-xl focus-visible:ring-2 focus-visible:ring-[rgba(74,144,226,0.35)]"
                  data-testid="waitlist-email-input"
                  aria-invalid={!!error}
                />
                {error && (
                  <p role="alert" className="text-sm text-red-600" data-testid="waitlist-error-text">{error}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="h-11 w-full rounded-xl bg-[var(--jb-blue)] text-white shadow-[0_10px_24px_rgba(74,144,226,0.28)] transition-colors hover:bg-[#3E7FCA] focus-visible:ring-2 focus-visible:ring-[var(--jb-mint)] focus-visible:ring-offset-2"
                data-testid="waitlist-submit-button"
              >
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Joining…</>) : "Join the Waitlist"}
              </Button>
              <p className="text-center text-xs text-slate-500">No spam. Unsubscribe anytime.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
